import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LogEvent } from '@core/enums/log-event';
import { LoggerService } from '@core/services/logger.service';
import { catchError, defer, map, Observable, of, shareReplay, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AgentActivationResponse } from '../models/agent-activation.model';

type AgentActivationCheck = {
  allowed: boolean;
  reason: 'active' | 'inactive' | 'error';
  activeCompanies: string[];
  errorStatus?: number;
};

@Injectable({
  providedIn: 'root',
})
export class PhoneAgentActivationService {
  // Fail-closed: any error resolves to inactive.
  readonly isActive$: Observable<boolean> = defer(() =>
    this.httpClient.get<AgentActivationResponse>(`${environment.externalUrls.bff}/telephony/agent-activation/me`)
  ).pipe(
    map((response): AgentActivationCheck => ({
      allowed: !!response?.active,
      reason: response?.active ? 'active' : 'inactive',
      activeCompanies: response?.companies ?? [],
    })),
    catchError((error: HttpErrorResponse) =>
      of<AgentActivationCheck>({ allowed: false, reason: 'error', activeCompanies: [], errorStatus: error?.status })
    ),
    tap((check) => this.logCheck(check)),
    map(({ allowed }) => allowed),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  constructor(
    private httpClient: HttpClient,
    private loggerService: LoggerService
  ) {}

  private logCheck(check: AgentActivationCheck) {
    if (!check.errorStatus) {
      this.loggerService.info(LogEvent.LIVECHAT_PHONE_AGENT_ACTIVATION_CHECK, check);
      return;
    }

    this.loggerService.warn(LogEvent.LIVECHAT_PHONE_AGENT_ACTIVATION_CHECK, check);
  }
}
